import { useQuery } from "@tanstack/react-query";

import {
  dragonTilesLiteByTypeQueryOptions,
  dragonTilesQueryOptions,
} from "@/api/dragon-tiles";
import { useDragonTileLabel } from "@/features/Connectors/byocTileDisplayName";
import { findParentDragonTileForInnerTileId } from "@/utils/dragonTiles";

/**
 * ID of the parent dragon tile for a frame's `inner_tile` id (for preview / test-send context).
 */
export function useParentDragonTileId(
  siteId: string,
  innerTileId: string | undefined,
): string | undefined {
  const { data } = useQuery({
    ...dragonTilesQueryOptions(siteId),
    enabled: Boolean(siteId && innerTileId?.trim()),
    select: (tiles) =>
      findParentDragonTileForInnerTileId(tiles, innerTileId)?.id,
  });
  return data;
}

/**
 * Category ("Browse", "Broadcast", ...) of the parent dragon tile for a
 * frame's `inner_tile` id; undefined while loading or when no parent is found.
 */
export function useParentDragonTileCategory(
  siteId: string,
  innerTileId: string | undefined,
): string | undefined {
  const { data } = useQuery({
    ...dragonTilesQueryOptions(siteId),
    enabled: Boolean(siteId && innerTileId?.trim()),
    select: (tiles) =>
      findParentDragonTileForInnerTileId(tiles, innerTileId)?.category,
  });
  return data;
}

/**
 * Whether a frame's `inner_tile` belongs to the site's BYOC dragon tile.
 * BYOC tiles are not in the default tiles list (that fetch sends no `type`),
 * so this reads the `type=byoc` listing instead. False while loading.
 */
export function useIsByocInnerTile(
  siteId: string,
  innerTileId: string | undefined,
  enabled = true,
): boolean {
  const { data } = useQuery({
    ...dragonTilesLiteByTypeQueryOptions(siteId, "byoc"),
    enabled: enabled && Boolean(siteId && innerTileId?.trim()),
    select: (tiles) =>
      Boolean(findParentDragonTileForInnerTileId(tiles, innerTileId)),
  });
  // A disabled query still serves cached data; the gate must win.
  return enabled && (data ?? false);
}

/**
 * Display name of the parent dragon tile for a frame's `inner_tile` id, for
 * the editor breadcrumbs; undefined while loading or when no parent is found.
 */
export function useParentDragonTileName(
  siteId: string,
  innerTileId: string | undefined,
): string | undefined {
  const getLabel = useDragonTileLabel(siteId);
  const { data: tile } = useQuery({
    ...dragonTilesQueryOptions(siteId),
    enabled: Boolean(siteId && innerTileId?.trim()),
    select: (tiles) => findParentDragonTileForInnerTileId(tiles, innerTileId),
  });
  return tile ? getLabel(tile) : undefined;
}
